/* Nothing at this address.
 *
 * Three things a 404 on a radio station has to do, in this order: say plainly
 * that the page is not there, take the blame if the link came from us, and make
 * it obvious that the audio is unaffected. Somebody who mistypes a catalogue
 * number while a set is playing does not want a cartoon — they want to know
 * their stream is still running and where the schedule is.
 *
 * So the way out is the stream itself: a real play control wired to the player
 * that is already mounted, not a link to a page with a play button on it.
 */

import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Section, slotHostNames } from '../components';
import { formatTime, tzLabel } from '../format';
import { usePlayer } from '../player';

export default function NotFound() {
  const { pathname } = useLocation();
  const { live, isPlaying, playLive, mode } = usePlayer();

  useEffect(() => {
    document.title = 'Nothing at this address · Higher Ground Radio';
  }, []);

  const onLive = mode === 'live' && isPlaying;

  return (
    <div className="nf">
      <style>{CSS}</style>

      <Section level={1} title="Nothing at this address" meta="HTTP 404" />

      <div className="nf__body">
        <p className="t-body">
          There is no page at <span className="mono-13 nf__path">{pathname}</span>. Bookings get
          moved and catalogue numbers get mistyped. Archive pages remain here even when a recording
          is not publicly playable, so the set you want may still be listed under a different
          address in the archive.
        </p>
        <p className="t-body">
          If you followed that link from somewhere on this site, the mistake is ours and we would
          like to know: tell us at <a href="mailto:highergroundradioshow@gmail.com">highergroundradioshow@gmail.com</a> and say which
          page you came from.
        </p>

        <div className="nf__air">
          <div className="mono dim">On the stream right now</div>
          <p className="nf__airline">{airLine(live)}</p>
          <p className="mono dim nf__airnote">
            The stream is not affected by this page. It is running at the bottom of the window and
            it does not stop when you navigate.
          </p>
        </div>

        <div className="nf__acts">
          <button type="button" className="btn" onClick={playLive} disabled={onLive}>
            {onLive ? 'Already playing' : 'Play the stream'}
          </button>
          <Link className="btn-ghost" to="/">
            What is on now
          </Link>
          <Link className="btn-ghost" to="/schedule">
            Schedule
          </Link>
          <Link className="btn-ghost" to="/archive">
            Archive
          </Link>
        </div>
      </div>
    </div>
  );
}

/** What the stream is serving, in one sentence, from the feed we already have. */
function airLine(live: ReturnType<typeof usePlayer>['live']): string {
  if (!live) return 'Asking the station what is on…';

  if (live.live && live.now) {
    const who = slotHostNames(live.now.title, live.now.hosts);
    return `On air — ${live.now.title}${who ? ` w/ ${who}` : ''}.`;
  }

  const fallback = live.fallback;
  const playing = fallback?.playing;
  const zone = tzLabel('station').split('·').pop()?.trim() ?? '';
  const next = live.next?.[0];
  const nextLine = next
    ? ` Next up at ${formatTime(next.starts_at, 'station')}${zone ? ` ${zone}` : ''}: ${next.title}.`
    : ' Nothing is booked after this yet.';

  if (playing) {
    const who = playing.hosts.map((host) => host.name).join(' & ');
    return `Off air — ${fallback?.label ?? 'the archive shuffle'} is playing ${playing.title}${
      who ? ` w/ ${who}` : ''
    }.${nextLine}`;
  }

  return `Off air — ${fallback?.label ?? 'the stream is up and quiet'}.${nextLine}`;
}

/* --------------------------------------------------------------------- css */

const CSS = `
.nf__body {
  padding: 30px clamp(12px, 4vw, 28px) 44px;
  display: flex; flex-direction: column; gap: 18px;
}
.nf__path { word-break: break-all; }
.nf__air {
  border: var(--bw) solid var(--edge); background: var(--surface);
  box-shadow: var(--sh-card);
  padding: 18px 20px; display: flex; flex-direction: column; gap: 10px;
  max-width: 68ch;
}
.nf__airline {
  margin: 0; font-size: 1.3125rem; line-height: 1.15;
  font-variation-settings: 'wdth' 96, 'wght' 800; text-transform: uppercase;
}
.nf .nf__airnote { margin: 0; line-height: 1.6; letter-spacing: .04em; text-transform: none; }
.nf__acts { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 6px; }
`;
